/**
 * cortex-admin の bot-status API contract（zod schema + 型）。
 * API（apps/api/admin）と Web（apps/web/cortex-admin）が共有する。
 *
 * 母艦（Mac mini）で動く collector が定期的に自身の稼働状況を POST し、admin がそれを表示する。
 * **母艦は Tailscale 経由でしか到達できないため、Cloud Run 側からポーリングできない。** push 一択。
 *
 * 収集側が死んだことを検知できるようにするのが本 contract の要。`checkedAt` は「変化があったとき」
 * ではなく **collector が走るたびに毎回更新する**。変化時のみ書く設計にすると、collector 自体が
 * 死んだときに最後の状態が永久に「最新」として残り、全て正常に見えてしまう。
 * 実際にバックアップ監視で同じ形の失敗をしている（弱い指標が更新され続け、外付けディスクが
 * 7 日間認識されていないのに毎日「OK」と報告し続けた）。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 母艦 bot の稼働状況レポートの API ↔ Web 型を共有し、二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/** 監視対象の種別。tmux セッション（bot 本体）と LaunchDaemon（定期ジョブ）を同じ形で扱う。 @graph-connects none */
export const BotServiceKindSchema = z.enum(['tmux', 'launchd']);
/**
 * 監視対象 1 件の状態。
 *
 * `running` だけでは足りない。プロセスが生きたまま仕事をしていない状態が実際に起きており
 * （engine 起動の 87% が SIGKILL で失敗し続けながら SSE 接続は正常だった）、それを捕まえるのが
 * `lastActivityAt` と `recentFailures`。
 *
 * @graph-connects none
 */
export const BotServiceStatusSchema = z.object({
    /** 識別子。tmux ならセッション名、launchd なら Label。 */
    name: z.string().min(1),
    kind: BotServiceKindSchema,
    /** 実行ユーザー（cortex / service-review / ryan / root 等）。 */
    user: z.string().min(1),
    running: z.boolean(),
    /** ログの最終更新時刻（ISO8601）。生きているが停滞している状態の検知に使う。 */
    lastActivityAt: z.iso.datetime().optional(),
    /** 直近ログ内の失敗件数。窓の定義は collector 側が持ち、`failureWindow` で説明する。 */
    recentFailures: z.number().int().nonnegative().optional(),
    /** `recentFailures` を数えた窓の説明（例: "直近 300 行"）。数値だけでは解釈できないため添える。 */
    failureWindow: z.string().optional(),
    /** 補足（起動からの経過、直近のエラー行など）。 */
    detail: z.string().optional(),
});
/**
 * ホスト全体の状態。
 *
 * メモリ / swap は bot が jetsam に刈られる前兆を見るために入れている。放置セッションが
 * 9.7GB を占めて reviewer の claude が SIGKILL され、PR のレビューが数日止まった実例がある。
 *
 * @graph-connects none
 */
export const BotHostMetricsSchema = z.object({
    memoryFreeBytes: z.number().nonnegative().optional(),
    memoryTotalBytes: z.number().nonnegative().optional(),
    swapUsedBytes: z.number().nonnegative().optional(),
    swapTotalBytes: z.number().nonnegative().optional(),
    diskFreeBytes: z.number().nonnegative().optional(),
    loadAverage1m: z.number().nonnegative().optional(),
    /** 最終バックアップからの経過時間。測定できない場合は null（0 や欠損で「正常」に倒さない）。 */
    backupAgeHours: z.number().nonnegative().nullable().optional(),
    /** バックアップ鮮度をどの経路で測ったか。測れていないこと自体を表示するために必要。 */
    backupSource: z.string().optional(),
});
/**
 * host のドキュメント ID として安全な文字種。`report`・`latest` の両方で共有し、
 * Firestore の `doc()` にスラッシュ等を渡してパスを混乱させられないようにする。
 * @graph-connects none
 */
const HOST_ID_SCHEMA = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'host は英小文字・数字・ハイフンのみ');
/**
 * collector が POST するレポート。
 *
 * @graph-connects none
 */
export const BotStatusReportRequestSchema = z.object({
    /** ホスト識別子。ドキュメント ID になる（現状は `mothership` の 1 件のみ）。 */
    host: HOST_ID_SCHEMA,
    /** collector が走った時刻（ISO8601）。**変化の有無に関係なく毎回更新する。** */
    checkedAt: z.iso.datetime(),
    /** collector スクリプトの版。表示の解釈が変わったときに気付けるようにする。 */
    collectorVersion: z.string().optional(),
    services: z.array(BotServiceStatusSchema),
    hostMetrics: BotHostMetricsSchema.optional(),
});
/** レポート受理のレスポンス。 @graph-connects none */
export const BotStatusReportResponseSchema = z.object({
    host: z.string(),
    storedAt: z.iso.datetime(),
});
/**
 * 保存済みレポート。POST された内容に、受理側が観測した事実を足したもの。
 *
 * @graph-connects none
 */
export const BotStatusRecordSchema = BotStatusReportRequestSchema.extend({
    /** 受理した時刻（サーバー時刻）。母艦の時計がずれていても鮮度を判定できるようにする。 */
    storedAt: z.iso.datetime(),
    /** 誰の資格情報で POST されたか（edge-router が注入する認証済み email）。 */
    reportedBy: z.string(),
});
/**
 * 最新レポートのレスポンス。
 *
 * **`serverTime` を必ず返す。** 鮮度は「今から見て `storedAt` がどれだけ古いか」で決まるが、
 * 閲覧端末の時計を基準にすると端末側のずれが鮮度判定に混入する。判定の基準時刻はサーバーが与える。
 *
 * `record` が null は「一度も受信していない」。collector 未設置とレポート途絶を UI で区別できるよう、
 * 404 ではなく null で返す。
 *
 * @graph-connects none
 */
export const BotStatusLatestResponseSchema = z.object({
    record: BotStatusRecordSchema.nullable(),
    serverTime: z.iso.datetime(),
});
/** 最新レポート取得の入力。host 省略時は既定ホスト。 @graph-connects none */
export const BotStatusLatestInputSchema = z.object({
    host: HOST_ID_SCHEMA.optional(),
});
//# sourceMappingURL=bot-status.js.map